import { Component, Input, ChangeDetectorRef, inject, OnChanges, SimpleChanges } from '@angular/core';
import { LogError, RunView } from '@memberjunction/core';
import {
    marketingExperimentEntity,
    marketingExperimentDecisionEntity,
    marketingAgentLearningEntity,
    marketingExperimentVariantEntity,
} from 'mj_generatedentities';
import { runAction, parseActionMessageJson, extractRecFromMessage, isRecommendDecisionResp } from '../../shared/run-action.util';

interface SprintBucket {
    label: string;
    value: number;
}

@Component({
    standalone: false,
    selector: 'mkt-ws-experiments-detail',
    template: `
<div *ngIf="loading" class="dt__loading"><i class="fa-solid fa-spinner fa-spin"></i> Loading experiment…</div>

<ng-container *ngIf="!loading && !experiment">
    <mkt-ws-page-head eyebrow="Experiment" title="No experiment selected"
        sub="Pick an experiment from the overview, backlog, or sprint review to drill in."
        [hasActions]="true">
        <div actions>
            <button class="dt__btn" type="button" (click)="navigate('experiments')"><i class="fa-solid fa-arrow-left"></i> Back to overview</button>
        </div>
    </mkt-ws-page-head>
    <mkt-ws-card *ngIf="recentExperiments.length > 0">
        <div class="dt__caps-ink" style="margin-bottom: 12px;">Recent experiments</div>
        <div class="dt__pick">
            <button *ngFor="let e of recentExperiments" type="button" class="dt__pick-row" (click)="openExperiment(e.ID)">
                <span class="dt__mono dt__caps">{{ e.Code || e.ID.slice(0,8) }}</span>
                <span class="dt__pick-name">{{ e.Name || e.Hypothesis }}</span>
                <mkt-ws-pill variant="muted">{{ e.ExperimentStatus }}</mkt-ws-pill>
            </button>
        </div>
    </mkt-ws-card>
</ng-container>

<ng-container *ngIf="!loading && experiment">
    <div class="dt__crumbs">
        <button type="button" class="dt__crumb" (click)="navigate('experiments')">Experiments</button>
        <span>›</span>
        <span class="dt__crumb-current">{{ experiment.ExperimentStatus }}</span>
        <span>›</span>
        <span class="dt__mono">{{ experiment.Code || experiment.ID.slice(0,8) }}</span>
    </div>
    <mkt-ws-page-head
        [eyebrow]="'Experiment · ' + (experiment.Code || '—') + ' · ' + (experiment.ExperimentTier || '—') + ' · ' + (experiment.Channel || '—')"
        [title]="experiment.Name || experiment.Hypothesis || 'Experiment'"
        [sub]="subText"
        [hasActions]="true">
        <div actions>
            <button class="dt__btn" type="button" [disabled]="busy" (click)="pickDecision('KILL')"><i class="fa-solid fa-xmark"></i> KILL</button>
            <button class="dt__btn" type="button" [disabled]="busy" (click)="pickDecision('ITERATE')"><i class="fa-solid fa-rotate-left"></i> ITERATE</button>
            <button class="dt__btn" type="button" [disabled]="busy" (click)="pickDecision('KEEP')"><i class="fa-solid fa-check"></i> KEEP</button>
            <button class="dt__btn dt__btn--primary" type="button" [disabled]="busy" (click)="pickDecision('GRADUATE')"><i class="fa-solid fa-arrow-trend-up"></i> Graduate</button>
        </div>
    </mkt-ws-page-head>

    <div *ngIf="pendingDecision" class="dt__override">
        <div class="dt__override-head">
            <i class="fa-solid fa-circle-exclamation"></i>
            <strong>Override the agent recommendation</strong>
            <span class="dt__sub">You picked <b>{{ pendingDecision }}</b>; agent recommended <b>{{ recommendation.decision || '—' }}</b>. Capture why so the next sprint review can learn from it.</span>
        </div>
        <textarea class="dt__textarea" rows="2" [(ngModel)]="overrideReason"
            placeholder="One line: why your call beats the agent's (e.g., 'Sample too small to trust the rec; rerunning with bigger budget')."></textarea>
        <div class="dt__override-actions">
            <button class="dt__btn dt__btn--primary" type="button" [disabled]="busy || !overrideReason.trim()" (click)="confirmDecision()">
                <i class="fa-solid fa-check"></i> Confirm {{ pendingDecision }}
            </button>
            <button class="dt__btn" type="button" [disabled]="busy" (click)="cancelDecision()">Cancel</button>
        </div>
    </div>

    <div *ngIf="busyMessage" class="dt__busy" [class.dt__busy--err]="busyError">{{ busyMessage }}</div>

    <!-- Summary strip -->
    <div class="dt__summary">
        <mkt-ws-card><div class="dt__caps">Sprints running</div><div class="dt__big">{{ sprintsRunning }}</div></mkt-ws-card>
        <mkt-ws-card><div class="dt__caps">Conversions · 30d</div><div class="dt__big">{{ totalConversions }}</div></mkt-ws-card>
        <mkt-ws-card><div class="dt__caps">Spend</div><div class="dt__big">{{ formatBudget(experiment.Budget) }}</div></mkt-ws-card>
        <mkt-ws-card><div class="dt__caps">Confidence</div><div class="dt__big">{{ experiment.ProposalConfidence || '—' }}</div></mkt-ws-card>
        <mkt-ws-card><div class="dt__caps">KEEP threshold</div><div class="dt__big">{{ experiment.KeepThreshold ?? '—' }}</div></mkt-ws-card>
    </div>

    <div class="dt__layout">
        <div class="dt__main">
            <mkt-ws-card [hasHead]="true">
                <div head><span class="dt__caps-ink">Hypothesis &amp; method</span></div>
                <div style="display:flex; flex-direction:column; gap:14px;">
                    <div>
                        <div class="dt__caps">Hypothesis</div>
                        <div class="dt__body">{{ experiment.Hypothesis || '—' }}</div>
                    </div>
                    <div *ngIf="experiment.Tactic">
                        <div class="dt__caps">Tactic</div>
                        <div class="dt__body">{{ experiment.Tactic }}</div>
                    </div>
                    <div>
                        <div class="dt__caps">Decision criteria</div>
                        <div class="dt__body dt__mono">
                            KEEP if ≥ {{ experiment.KeepThreshold ?? '?' }} ·
                            ITERATE if {{ experiment.IterateRangeMin ?? '?' }}–{{ experiment.IterateRangeMax ?? '?' }} ·
                            KILL if ≤ {{ experiment.KillThreshold ?? '?' }}
                        </div>
                    </div>
                    <div *ngIf="experiment.StrategicConnectionNote">
                        <div class="dt__caps">Strategic connection</div>
                        <div class="dt__body">{{ experiment.StrategicConnectionNote }}</div>
                    </div>
                    <div *ngIf="experiment.PriorLearningNotes">
                        <div class="dt__caps">Prior-learning notes</div>
                        <div class="dt__body">{{ experiment.PriorLearningNotes }}</div>
                    </div>
                </div>
            </mkt-ws-card>

            <mkt-ws-card [hasHead]="true">
                <div head>
                    <span class="dt__caps-ink">Observed performance</span>
                    <span class="dt__sub">Drives the agent rec + the saved decision · update as data lands</span>
                </div>
                <div class="dt__obs-fields">
                    <label>
                        <span class="dt__caps">Observed metric</span>
                        <input type="number" step="any" class="dt__input" [(ngModel)]="observation.observedMetric" placeholder="e.g., 1.6" />
                    </label>
                    <label>
                        <span class="dt__caps">Conversions count</span>
                        <input type="number" step="1" class="dt__input" [(ngModel)]="observation.conversionsCount" placeholder="e.g., 18" />
                    </label>
                    <button class="dt__btn" type="button" [disabled]="recommendingDecision" (click)="loadRecommendation()">
                        <i class="fa-solid fa-rotate"></i> Refresh recommendation
                    </button>
                </div>
                <ng-container *ngIf="segmentResults.length > 0">
                    <div class="dt__caps-ink" style="margin-top: 14px;">Per-segment results</div>
                    <div class="dt__sub" style="margin-bottom: 8px;">From the SegmentCuts declared at design time. Recorded on KEEP/ITERATE/KILL submission.</div>
                    <table class="dt__table">
                        <thead>
                            <tr><th>Segment</th><th class="dt__t-num">Observed metric</th><th class="dt__t-num">Conversions</th></tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let seg of segmentResults">
                                <td class="dt__t-name">{{ seg.label }}</td>
                                <td class="dt__t-num"><input type="number" step="any" class="dt__input dt__input--sm" [(ngModel)]="seg.observedMetric" /></td>
                                <td class="dt__t-num"><input type="number" step="1" class="dt__input dt__input--sm" [(ngModel)]="seg.conversionsCount" /></td>
                            </tr>
                        </tbody>
                    </table>
                </ng-container>
            </mkt-ws-card>

            <mkt-ws-card [hasHead]="true" *ngIf="conversionsBySprint.length > 0">
                <div head>
                    <span class="dt__caps-ink">Conversions by sprint</span>
                    <span class="dt__sub">Floor = {{ experiment.KeepThreshold ?? 'KEEP' }}</span>
                </div>
                <div class="dt__chart">
                    <div *ngFor="let b of conversionsBySprint" class="dt__chart-bar">
                        <div class="dt__chart-fill" [style.height.%]="barPct(b.value)" [class.dt__chart-fill--good]="b.value >= (experiment.KeepThreshold ?? 0)"></div>
                        <div class="dt__caps">{{ b.label }}</div>
                        <div class="dt__mono dt__chart-num">{{ b.value }}</div>
                    </div>
                </div>
            </mkt-ws-card>

            <mkt-ws-card [hasHead]="true">
                <div head>
                    <span class="dt__caps-ink">Variants</span>
                    <span class="dt__sub">A/B branches that share thresholds with the parent experiment</span>
                </div>
                <div *ngIf="canPromoteVariants" class="dt__sub" style="margin-bottom: 8px;">Parent decided — promote a non-control variant to spawn a follow-up experiment.</div>
                <div *ngIf="!canPromoteVariants && variants.length > 0" class="dt__sub" style="margin-bottom: 8px;">Decide the parent first to enable variant promotion.</div>
                <table *ngIf="variants.length > 0" class="dt__table">
                    <thead>
                        <tr><th>Variant</th><th>Description</th><th class="dt__t-num">Allocation</th><th class="dt__t-actions"></th></tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let v of variants">
                            <td class="dt__t-name">
                                <span>{{ v.Name }}</span>
                                <mkt-ws-pill *ngIf="v.IsControl" variant="good" style="margin-left: 8px;">Control</mkt-ws-pill>
                            </td>
                            <td class="dt__sub">{{ v.Description || '—' }}</td>
                            <td class="dt__t-num dt__mono">{{ v.AllocationPct ?? '—' }}%</td>
                            <td class="dt__t-actions">
                                <button *ngIf="canPromoteVariants && !v.IsControl" class="dt__btn" type="button"
                                    [disabled]="promotingVariantId === v.ID" (click)="promoteVariant(v)">
                                    <i class="fa-solid fa-arrow-up-right-from-square"></i>
                                    {{ promotingVariantId === v.ID ? 'Promoting…' : 'Promote winner' }}
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div *ngIf="variants.length === 0" class="dt__sub" style="padding: 8px 0;">No variants yet. Add one below to A/B test against the experiment baseline.</div>

                <div class="dt__variant-form" *ngIf="!variantBusy && !variantFormOpen">
                    <button class="dt__btn" type="button" (click)="variantFormOpen = true">
                        <i class="fa-solid fa-plus"></i> Add variant
                    </button>
                </div>
                <div class="dt__variant-form" *ngIf="variantFormOpen">
                    <div class="dt__variant-fields">
                        <label>
                            <span class="dt__caps">Name</span>
                            <input type="text" class="dt__input" [(ngModel)]="newVariant.name" placeholder="e.g., 3-step variant" />
                        </label>
                        <label>
                            <span class="dt__caps">Description</span>
                            <input type="text" class="dt__input" [(ngModel)]="newVariant.description" placeholder="What's different about this branch" />
                        </label>
                        <label>
                            <span class="dt__caps">Allocation %</span>
                            <input type="number" min="0" max="100" step="1" class="dt__input dt__input--num" [(ngModel)]="newVariant.allocationPct" placeholder="50" />
                        </label>
                        <label class="dt__variant-control">
                            <input type="checkbox" [(ngModel)]="newVariant.isControl" /> Control
                        </label>
                        <button class="dt__btn dt__btn--primary" type="button" [disabled]="variantBusy || !newVariant.name" (click)="addVariant()">
                            <i class="fa-solid fa-check"></i> {{ variantBusy ? 'Adding…' : 'Add variant' }}
                        </button>
                        <button class="dt__btn" type="button" [disabled]="variantBusy" (click)="cancelVariant()">Cancel</button>
                    </div>
                    <div *ngIf="variantError" class="dt__sub" style="color: var(--mj-status-error, #be123c);">{{ variantError }}</div>
                </div>
            </mkt-ws-card>
        </div>

        <div class="dt__side">
            <mkt-ws-card [hasHead]="true">
                <div head>
                    <span class="dt__caps-ink">Agent recommendation</span>
                    <mkt-ws-pill *ngIf="recommendation.decision" [variant]="recVariant">{{ recommendation.decision }}</mkt-ws-pill>
                </div>
                <div *ngIf="recommendingDecision" class="dt__sub"><i class="fa-solid fa-spinner fa-spin"></i> Asking the agent…</div>
                <ng-container *ngIf="!recommendingDecision && recommendation.decision">
                    <div class="dt__rec-title">{{ recommendation.decision }}{{ recommendation.confidence ? ' · ' + recommendation.confidence : '' }}</div>
                    <div class="dt__sub" style="margin-top: 8px;">{{ recommendation.rationale || '—' }}</div>
                    <div *ngIf="recommendation.nextActionHint" class="dt__rec-hint">
                        <i class="fa-solid fa-lightbulb"></i>
                        <strong>Next move:</strong> {{ recommendation.nextActionHint }}
                    </div>
                </ng-container>
                <ng-container *ngIf="!recommendingDecision && !recommendation.decision">
                    <button class="dt__btn" type="button" (click)="loadRecommendation()"><i class="fa-solid fa-wand-magic-sparkles"></i> Ask the agent</button>
                </ng-container>
            </mkt-ws-card>

            <mkt-ws-card [hasHead]="true" [noPadding]="true" *ngIf="relatedFindings.length > 0">
                <div head><span class="dt__caps-ink">Related findings</span></div>
                <button *ngFor="let f of relatedFindings" type="button" class="dt__finding" (click)="openLearning(f.ID)">
                    <mkt-ws-pill [variant]="f.Confidence === 'High' ? 'good' : f.Confidence === 'Low' ? 'bad' : 'warn'">{{ f.Confidence }}</mkt-ws-pill>
                    <div>
                        <div class="dt__finding-title">{{ f.Finding }}</div>
                        <div class="dt__sub" *ngIf="f.DateRecorded">{{ formatDate(f.DateRecorded) }}</div>
                    </div>
                </button>
            </mkt-ws-card>

            <mkt-ws-card [hasHead]="true" *ngIf="experiment.Evidence">
                <div head><span class="dt__caps-ink">Evidence</span></div>
                <div class="dt__body">{{ experiment.Evidence }}</div>
            </mkt-ws-card>
        </div>
    </div>
</ng-container>
    `,
    styles: [`
        :host { display: block; }
        .dt__loading { padding: 60px 20px; text-align: center; color: var(--mj-text-secondary, #71717a); }
        .dt__btn { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; height: 30px; border: 1px solid var(--mj-border-default, #e0e0e0); background: var(--mj-bg-surface, #fff); border-radius: 6px; font: inherit; font-size: 12px; cursor: pointer; color: var(--mj-text-primary, #1a1a1a); }
        .dt__btn:hover { background: var(--mj-bg-page, #f5f5f5); }
        .dt__btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .dt__btn--primary { background: var(--mj-text-primary, #1a1a1a); color: var(--mj-bg-surface, #fff); border-color: var(--mj-text-primary, #1a1a1a); }

        .dt__caps, .dt__caps-ink { font-size: 10.5px; letter-spacing: 0.11em; text-transform: uppercase; font-weight: 500; color: var(--mj-text-secondary, #71717a); margin-bottom: 6px; }
        .dt__caps-ink { color: var(--mj-text-primary, #1a1a1a); }
        .dt__sub { font-size: 11.5px; color: var(--mj-text-secondary, #71717a); }
        .dt__big { font-family: ui-monospace, monospace; font-variant-numeric: tabular-nums; font-size: 22px; color: var(--mj-text-primary, #1a1a1a); margin-top: 2px; }
        .dt__mono { font-family: ui-monospace, monospace; font-variant-numeric: tabular-nums; }
        .dt__body { font-size: 13px; line-height: 1.6; color: var(--mj-text-primary, #1a1a1a); }

        .dt__crumbs { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--mj-text-secondary, #71717a); margin-bottom: 14px; }
        .dt__crumb { background: none; border: none; padding: 0; font: inherit; font-size: 12px; color: var(--mj-text-secondary, #71717a); cursor: pointer; }
        .dt__crumb:hover { color: var(--mj-text-primary, #1a1a1a); }
        .dt__crumb-current { color: var(--mj-text-primary, #1a1a1a); }

        .dt__busy { padding: 10px 14px; border-radius: 6px; background: var(--mj-color-success-50, #ecfdf5); color: var(--mj-color-success-700, #047857); border: 1px solid var(--mj-color-success-100, #d1fae5); margin-bottom: 16px; font-size: 12.5px; }
        .dt__busy--err { background: var(--mj-color-error-50, #fff1f2); color: var(--mj-status-error, #be123c); border-color: var(--mj-color-error-100, #fecdd3); }

        .dt__summary { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 24px; }

        .dt__layout { display: grid; grid-template-columns: 2fr 1fr; gap: 24px; align-items: start; }
        .dt__main, .dt__side { display: flex; flex-direction: column; gap: 16px; }

        .dt__chart { display: flex; align-items: end; height: 160px; gap: 8px; padding: 0 4px; }
        .dt__chart-bar { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 4px; height: 100%; justify-content: flex-end; }
        .dt__chart-fill { width: 100%; background: var(--mj-text-primary, #1a1a1a); border-radius: 2px; min-height: 4px; }
        .dt__chart-fill--good { background: var(--mj-color-success-700, #047857); }
        .dt__chart-num { font-size: 11px; }

        .dt__table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .dt__table th { font-size: 10.5px; letter-spacing: 0.11em; text-transform: uppercase; font-weight: 500; color: var(--mj-text-secondary, #71717a); padding: 8px 12px; text-align: left; background: var(--mj-bg-page, #f5f5f5); }
        .dt__table th.dt__t-num { text-align: right; }
        .dt__table td { padding: 8px 12px; border-top: 1px solid var(--mj-border-default, #e0e0e0); }
        .dt__table td.dt__t-name { font-weight: 500; color: var(--mj-text-primary, #1a1a1a); }
        .dt__table td.dt__t-num { text-align: right; }

        .dt__rec-title { font-size: 14px; font-weight: 500; color: var(--mj-text-primary, #1a1a1a); }
        .dt__rec-hint { margin-top: 10px; padding: 8px 12px; background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-left: 3px solid var(--mj-color-warning-700, #b45309); border-radius: 6px; font-size: 12px; color: var(--mj-text-primary, #1a1a1a); display: flex; align-items: center; gap: 8px; }
        .dt__rec-hint .fa-lightbulb { color: var(--mj-color-warning-700, #b45309); }

        .dt__finding { display: flex; align-items: flex-start; gap: 10px; padding: 12px 16px; border: none; background: transparent; width: 100%; text-align: left; cursor: pointer; border-bottom: 1px solid var(--mj-border-default, #e0e0e0); font: inherit; transition: background 0.15s; }
        .dt__finding:last-child { border-bottom: none; }
        .dt__finding:hover { background: var(--mj-bg-page, #f5f5f5); }
        .dt__finding-title { font-size: 12.5px; font-weight: 500; color: var(--mj-text-primary, #1a1a1a); line-height: 1.4; }

        .dt__variant-form { margin-top: 12px; }
        .dt__variant-fields { display: flex; gap: 12px; align-items: flex-end; flex-wrap: wrap; }
        .dt__variant-fields label { display: flex; flex-direction: column; gap: 4px; font-size: 11.5px; color: var(--mj-text-secondary, #71717a); }
        .dt__variant-control { flex-direction: row !important; align-items: center; gap: 6px !important; padding-bottom: 8px; }
        .dt__input { padding: 6px 10px; height: 32px; font: inherit; font-size: 12.5px; border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 6px; background: var(--mj-bg-surface, #fff); color: var(--mj-text-primary, #1a1a1a); width: 220px; }
        .dt__input--num { width: 90px; }
        .dt__input--sm { width: 110px; }
        .dt__input:focus { outline: 2px solid var(--mj-brand-primary, #6366f1); outline-offset: -1px; }

        .dt__obs-fields { display: flex; gap: 12px; align-items: flex-end; flex-wrap: wrap; }
        .dt__obs-fields label { display: flex; flex-direction: column; gap: 4px; font-size: 11.5px; color: var(--mj-text-secondary, #71717a); }

        .dt__override { margin-bottom: 16px; padding: 12px 14px; background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-left: 3px solid var(--mj-color-warning-700, #b45309); border-radius: 6px; color: var(--mj-text-primary, #1a1a1a); }
        .dt__override-head { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; font-size: 12.5px; color: var(--mj-text-primary, #1a1a1a); flex-wrap: wrap; }
        .dt__override-head .fa-circle-exclamation { color: var(--mj-color-warning-700, #b45309); }
        .dt__override-actions { display: flex; gap: 6px; margin-top: 8px; }
        .dt__textarea { width: 100%; box-sizing: border-box; padding: 8px 10px; font: inherit; font-size: 12.5px; border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 6px; background: var(--mj-bg-surface, #fff); color: var(--mj-text-primary, #1a1a1a); resize: vertical; min-height: 48px; }
        .dt__textarea:focus { outline: 2px solid var(--mj-brand-primary, #6366f1); outline-offset: -1px; }

        .dt__table th.dt__t-actions, .dt__table td.dt__t-actions { width: 1%; white-space: nowrap; text-align: right; }

        .dt__pick { display: flex; flex-direction: column; gap: 4px; }
        .dt__pick-row { display: flex; align-items: center; gap: 12px; padding: 10px 12px; background: transparent; border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 6px; cursor: pointer; font: inherit; text-align: left; transition: background 0.15s; }
        .dt__pick-row:hover { background: var(--mj-bg-page, #f5f5f5); }
        .dt__pick-name { flex: 1; font-size: 13px; color: var(--mj-text-primary, #1a1a1a); }

        @media (max-width: 1024px) {
            .dt__summary { grid-template-columns: repeat(2, 1fr); }
            .dt__layout { grid-template-columns: 1fr; }
        }
    `]
})
export class ExperimentsDetailSection implements OnChanges {
    private _companyId = '';
    private _selectedId = '';

    @Input() set companyId(v: string) {
        if (v && v !== this._companyId) {
            this._companyId = v;
            void this.load();
        }
    }
    get companyId(): string { return this._companyId; }

    @Input() set selectedId(v: string) {
        if (v !== this._selectedId) {
            this._selectedId = v;
            void this.load();
        }
    }
    get selectedId(): string { return this._selectedId; }

    @Input() navigate: (path: string) => void = () => {};
    @Input() navigateWithId: (path: string, id?: string) => void = () => {};

    private cdr = inject(ChangeDetectorRef);

    loading = false;
    experiment: marketingExperimentEntity | null = null;
    recentExperiments: marketingExperimentEntity[] = [];
    variants: marketingExperimentVariantEntity[] = [];
    decisions: marketingExperimentDecisionEntity[] = [];
    relatedFindings: marketingAgentLearningEntity[] = [];
    conversionsBySprint: SprintBucket[] = [];
    totalConversions = 0;
    sprintsRunning = 0;

    recommendation: { decision: string; confidence: string; rationale: string; nextActionHint: string } = {
        decision: '', confidence: '', rationale: '', nextActionHint: '',
    };
    recommendingDecision = false;

    busy = false;
    busyMessage = '';
    busyError = false;

    variantFormOpen = false;
    variantBusy = false;
    variantError = '';
    newVariant: { name: string; description: string; allocationPct: number; isControl: boolean } = {
        name: '', description: '', allocationPct: 50, isControl: false,
    };

    /** Live observation values, drive both the recommendation refresh and Decide submission. */
    observation: { observedMetric: number | null; conversionsCount: number | null } = {
        observedMetric: null, conversionsCount: null,
    };

    /** Per-segment outcome rows. Hydrated from experiment.SegmentCuts when SegmentCuts is set. */
    segmentResults: { label: string; observedMetric: number | null; conversionsCount: number | null }[] = [];

    /** Override reason flow — when user picks a decision that differs from agent rec. */
    pendingDecision: 'KEEP' | 'ITERATE' | 'KILL' | 'GRADUATE' | null = null;
    overrideReason = '';

    /** Variant promotion state. */
    promotingVariantId: string | null = null;

    ngOnChanges(_changes: SimpleChanges): void { /* relies on @Input setters */ }

    cancelVariant(): void {
        this.variantFormOpen = false;
        this.variantError = '';
        this.newVariant = { name: '', description: '', allocationPct: 50, isControl: false };
    }

    async addVariant(): Promise<void> {
        if (!this.experiment || !this.newVariant.name) return;
        this.variantBusy = true;
        this.variantError = '';
        this.cdr.detectChanges();
        const res = await runAction('Add Variant To Experiment', [
            { Name: 'CompanyID', Value: this.experiment.CompanyID },
            { Name: 'ExperimentID', Value: this.experiment.ID },
            { Name: 'Name', Value: this.newVariant.name },
            ...(this.newVariant.description ? [{ Name: 'Description', Value: this.newVariant.description }] : []),
            { Name: 'AllocationPct', Value: String(this.newVariant.allocationPct ?? 0) },
            { Name: 'IsControl', Value: this.newVariant.isControl ? 'true' : 'false' },
        ]);
        this.variantBusy = false;
        if (res.success) {
            this.cancelVariant();
            await this.load();
        } else {
            this.variantError = res.message || 'Failed to add variant.';
            this.cdr.detectChanges();
        }
    }

    get subText(): string {
        if (!this.experiment) return '';
        const decisionDate = this.experiment.DecisionDate ? new Date(this.experiment.DecisionDate).toLocaleDateString() : '?';
        return `Decision due ${decisionDate}.`;
    }

    get recVariant(): 'good' | 'warn' | 'bad' | 'muted' {
        const d = (this.recommendation.decision || '').toLowerCase();
        if (d.includes('keep') || d.includes('graduate')) return 'good';
        if (d.includes('iterate') || d.includes('hold')) return 'warn';
        if (d.includes('kill')) return 'bad';
        return 'muted';
    }

    formatBudget(b: number | null | undefined): string {
        if (b == null) return '—';
        if (b === 0) return '$0';
        return `$${Math.round(b).toLocaleString()}`;
    }
    formatDate(d: Date | null | undefined): string {
        if (!d) return '—';
        return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    }

    barPct(value: number): number {
        const max = Math.max(...this.conversionsBySprint.map(b => b.value), this.experiment?.KeepThreshold ?? 0, 1);
        return max === 0 ? 4 : Math.max(4, (value / max) * 100);
    }

    private async load(): Promise<void> {
        if (!this._companyId) return;
        if (!this._selectedId) {
            await this.loadRecentForFallback();
            return;
        }
        this.loading = true;
        this.recommendation = { decision: '', confidence: '', rationale: '', nextActionHint: '' };
        this.cdr.detectChanges();
        try {
            const rv = new RunView();
            const [expRes, varRes, decRes] = await rv.RunViews([
                {
                    EntityName: 'Experiments',
                    ExtraFilter: `ID='${this._selectedId}'`,
                    MaxRows: 1,
                    ResultType: 'entity_object',
                },
                {
                    EntityName: 'Experiment Variants',
                    ExtraFilter: `ExperimentID='${this._selectedId}'`,
                    OrderBy: 'IsControl DESC, Name ASC',
                    ResultType: 'simple',
                },
                {
                    EntityName: 'Experiment Decisions',
                    ExtraFilter: `ExperimentID='${this._selectedId}'`,
                    ResultType: 'simple',
                },
            ]);
            if (expRes.Success && expRes.Results && expRes.Results.length > 0) {
                this.experiment = expRes.Results[0] as marketingExperimentEntity;
                if (this.experiment.StartDate) {
                    const start = new Date(this.experiment.StartDate).getTime();
                    const ms = Date.now() - start;
                    this.sprintsRunning = Math.max(0, Math.floor(ms / (14 * 24 * 60 * 60 * 1000)));
                }
                this.segmentResults = this.parseSegmentLabels(this.experiment.SegmentCuts).map(label => ({
                    label, observedMetric: null, conversionsCount: null,
                }));
                this.observation = { observedMetric: null, conversionsCount: null };
                this.pendingDecision = null;
                this.overrideReason = '';
            }
            if (varRes.Success) this.variants = varRes.Results as marketingExperimentVariantEntity[];
            if (decRes.Success) this.decisions = decRes.Results as marketingExperimentDecisionEntity[];

            // Conversions, related findings, and recommendation are independent
            // given `this.experiment` is loaded — run them in parallel.
            await Promise.all([
                this.loadConversions(),
                this.loadRelatedFindings(),
                this.loadRecommendation(),
            ]);
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[ExperimentsDetail] load failed: ${msg}`);
        } finally {
            this.loading = false;
            this.cdr.detectChanges();
        }
    }

    private async loadConversions(): Promise<void> {
        if (!this.experiment) return;
        try {
            const rv = new RunView();
            const linkRes = await rv.RunView({
                EntityName: 'Experiment Contents',
                ExtraFilter: `ExperimentID='${this.experiment.ID}'`,
                ResultType: 'simple',
            });
            if (!linkRes.Success || !linkRes.Results || linkRes.Results.length === 0) return;
            const contentIds = (linkRes.Results as Array<{ ContentID?: string }>)
                .map(r => r.ContentID)
                .filter((x): x is string => !!x);
            if (contentIds.length === 0) return;
            const inList = contentIds.map(id => `'${id}'`).join(',');
            const metricRes = await rv.RunView({
                EntityName: 'Content Performance Metrics',
                ExtraFilter: `ContentID IN (${inList})`,
                OrderBy: 'MetricDate ASC',
                ResultType: 'simple',
            });
            if (!metricRes.Success || !metricRes.Results) return;
            const rows = metricRes.Results as Array<{ MetricDate?: string | Date; ConversionCount?: number }>;
            const buckets: Record<string, number> = {};
            const sprintDuration = 14 * 24 * 60 * 60 * 1000;
            const sprintStart = this.experiment.StartDate ? new Date(this.experiment.StartDate).getTime() : 0;
            for (const r of rows) {
                if (!r.MetricDate) continue;
                const d = new Date(r.MetricDate).getTime();
                const offset = sprintStart > 0 ? Math.floor((d - sprintStart) / sprintDuration) : Math.floor(d / sprintDuration);
                const key = `Sp ${offset >= 0 ? offset + 1 : offset}`;
                buckets[key] = (buckets[key] || 0) + (r.ConversionCount || 0);
            }
            const entries = Object.entries(buckets);
            this.conversionsBySprint = entries.slice(-7).map(([label, value]) => ({ label, value }));
            this.totalConversions = rows.reduce((s, r) => s + (r.ConversionCount || 0), 0);
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[ExperimentsDetail] loadConversions failed: ${msg}`);
        }
    }

    private async loadRelatedFindings(): Promise<void> {
        if (!this.experiment) return;
        try {
            const rv = new RunView();
            // Escape both quote and LIKE-pattern metachars so a future tactic
            // string carrying %, _, or [ doesn't leak through as a wildcard.
            // T-SQL: wrapping a metachar in [] makes it literal (e.g. [%]).
            const tactic = (this.experiment.Tactic || '')
                .replace(/'/g, "''")
                .replace(/[\[%_]/g, ch => `[${ch}]`);
            const filter = tactic
                ? `CompanyID='${this.experiment.CompanyID}' AND (Finding LIKE '%${tactic}%' OR Evidence LIKE '%${tactic}%')`
                : `CompanyID='${this.experiment.CompanyID}'`;
            const r = await rv.RunView<marketingAgentLearningEntity>({
                EntityName: 'Agent Learnings',
                ExtraFilter: filter,
                OrderBy: 'DateRecorded DESC',
                MaxRows: 5,
                ResultType: 'simple',
            });
            if (r.Success && r.Results) this.relatedFindings = r.Results;
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[ExperimentsDetail] loadRelatedFindings failed: ${msg}`);
        }
    }

    async loadRecommendation(): Promise<void> {
        if (!this.experiment) return;
        this.recommendingDecision = true;
        this.cdr.detectChanges();

        // Use the user-entered observation if present; otherwise synthesize plausible values
        // (placeholder until CPM-by-experiment aggregation lands) so the surface still renders.
        const startDate = this.experiment.StartDate ? new Date(this.experiment.StartDate) : new Date();
        const weeksRunning = Math.max(1, Math.floor((Date.now() - startDate.getTime()) / (7 * 24 * 60 * 60 * 1000)));
        const baseline = this.experiment.MetricBaseline ?? 1;
        const observedMetric = this.observation.observedMetric ?? baseline * 1.4;
        const conversionsCount = this.observation.conversionsCount ?? Math.max(0, Math.round(observedMetric * 10));

        const res = await runAction('Recommend Decision', [
            { Name: 'CompanyID', Value: this.experiment.CompanyID },
            { Name: 'ExperimentID', Value: this.experiment.ID },
            { Name: 'ObservedMetric', Value: String(observedMetric) },
            { Name: 'ConversionsCount', Value: String(conversionsCount) },
            { Name: 'WeeksRunning', Value: String(weeksRunning) },
        ]);
        if (res.success) {
            const parsed = parseActionMessageJson(res.message, isRecommendDecisionResp);
            if (parsed?.decision) {
                this.recommendation = {
                    decision: parsed.decision,
                    confidence: parsed.confidence || '',
                    rationale: parsed.rationale || '',
                    nextActionHint: parsed.nextActionHint || '',
                };
            } else {
                // Recommend Decision returns plain text "Recommend X (Y)." rather
                // than JSON; pull the keyword out so override comparison can work.
                const fromText = extractRecFromMessage(res.message);
                this.recommendation = fromText
                    ? { decision: fromText.decision, confidence: fromText.confidence, rationale: fromText.rationale, nextActionHint: '' }
                    : { decision: 'See message', confidence: '', rationale: res.message, nextActionHint: '' };
            }
        } else {
            this.recommendation = { decision: '', confidence: '', rationale: `Error: ${res.message}`, nextActionHint: '' };
        }
        this.recommendingDecision = false;
        this.cdr.detectChanges();
    }

    private async loadRecentForFallback(): Promise<void> {
        if (!this._companyId) return;
        try {
            const rv = new RunView();
            const r = await rv.RunView<marketingExperimentEntity>({
                EntityName: 'Experiments',
                ExtraFilter: `CompanyID='${this._companyId}'`,
                OrderBy: 'DecisionDate ASC, __mj_CreatedAt DESC',
                MaxRows: 10,
                ResultType: 'simple',
            });
            if (r.Success) this.recentExperiments = r.Results || [];
        } catch (err: unknown) {
            LogError(`[ExperimentsDetail] loadRecent failed: ${err instanceof Error ? err.message : String(err)}`);
        } finally {
            this.cdr.detectChanges();
        }
    }

    openExperiment(id: string): void {
        this.navigateWithId('experiments/detail', id);
    }
    openLearning(id: string): void {
        this.navigateWithId('experiments/learnings/detail', id);
    }

    /**
     * Parse SegmentCuts (declared at design time) into an ordered list of labels.
     * Splits on commas and newlines; trims; drops empties.
     */
    private parseSegmentLabels(raw: string | null | undefined): string[] {
        if (!raw) return [];
        return raw.split(/[,\n]/).map(s => s.trim()).filter(s => s.length > 0);
    }

    get canPromoteVariants(): boolean {
        return (this.experiment?.ExperimentStatus || '').toLowerCase() === 'decided';
    }

    /**
     * User clicked a decision button. If their pick differs from the agent's recommendation,
     * surface the override-reason input before committing. When the agent has no usable
     * recommendation (action returned non-JSON or errored), we skip the override prompt —
     * there's nothing to override.
     */
    pickDecision(decision: 'KEEP' | 'ITERATE' | 'KILL' | 'GRADUATE'): void {
        if (!this.experiment) return;
        if (decision === 'ITERATE') {
            void this.onIterate();
            return;
        }
        if (decision === 'GRADUATE') {
            void this.onComplete('Evergreen');
            return;
        }
        const agentRec = (this.recommendation.decision || '').toUpperCase();
        const recAvailable = agentRec.length > 0 && !agentRec.includes('SEE MESSAGE');
        if (!recAvailable || agentRec.includes(decision)) {
            void this.commitDecide(decision, '');
            return;
        }
        this.pendingDecision = decision;
        this.overrideReason = '';
        this.cdr.detectChanges();
    }

    confirmDecision(): void {
        if (!this.pendingDecision || !this.overrideReason.trim()) return;
        const decision = this.pendingDecision;
        const reason = this.overrideReason.trim();
        this.pendingDecision = null;
        if (decision === 'GRADUATE') {
            void this.onComplete('Evergreen');
        } else if (decision === 'ITERATE') {
            void this.onIterate();
        } else {
            void this.commitDecide(decision, reason);
        }
    }

    cancelDecision(): void {
        this.pendingDecision = null;
        this.overrideReason = '';
        this.cdr.detectChanges();
    }

    private async commitDecide(decision: 'KEEP' | 'ITERATE' | 'KILL', overrideReason: string): Promise<void> {
        if (!this.experiment) return;
        this.busy = true;
        this.busyError = false;
        this.busyMessage = `Recording ${decision}…`;
        this.cdr.detectChanges();
        // Action contract is PascalCase: Keep / Iterate / Kill.
        const decisionName = decision.charAt(0) + decision.slice(1).toLowerCase();
        const startDate = this.experiment.StartDate ? new Date(this.experiment.StartDate) : new Date();
        const weeksRunning = Math.max(1, Math.floor((Date.now() - startDate.getTime()) / (7 * 24 * 60 * 60 * 1000)));
        const params: { Name: string; Value: string }[] = [
            { Name: 'CompanyID', Value: this.experiment.CompanyID },
            { Name: 'ExperimentID', Value: this.experiment.ID },
            { Name: 'DecisionName', Value: decisionName },
            { Name: 'WeeksRunning', Value: String(weeksRunning) },
        ];
        if (this.observation.observedMetric != null) params.push({ Name: 'ObservedMetric', Value: String(this.observation.observedMetric) });
        if (this.observation.conversionsCount != null) params.push({ Name: 'ConversionsCount', Value: String(this.observation.conversionsCount) });
        if (overrideReason) params.push({ Name: 'OverrideReason', Value: overrideReason });
        const populatedSegments = this.segmentResults
            .filter(s => s.observedMetric != null && s.conversionsCount != null)
            .map(s => ({ label: s.label, observedMetric: s.observedMetric, conversionsCount: s.conversionsCount }));
        if (populatedSegments.length > 0) {
            params.push({ Name: 'SegmentResults', Value: JSON.stringify(populatedSegments) });
        }
        const res = await runAction('Decide Experiment', params);
        if (res.success) {
            this.busyMessage = overrideReason ? `Decided ${decision}. Override reason captured.` : `Decided ${decision}.`;
            await this.load();
        } else {
            this.busyError = true;
            this.busyMessage = `Decide failed (${res.resultCode}): ${res.message}`;
        }
        this.busy = false;
        this.cdr.detectChanges();
    }

    /**
     * Promote a non-control variant to a follow-up Proposed experiment, seeded
     * with the variant's name + description as the next hypothesis. Uses the
     * existing Iterate Experiment action — parent must be Decided.
     */
    async promoteVariant(variant: marketingExperimentVariantEntity): Promise<void> {
        if (!this.experiment) return;
        this.promotingVariantId = variant.ID;
        this.busy = true;
        this.busyError = false;
        this.busyMessage = `Promoting variant "${variant.Name}"…`;
        this.cdr.detectChanges();
        const newDecisionDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
        const baseName = this.experiment.Name ?? this.experiment.Hypothesis?.slice(0, 60) ?? 'Experiment';
        const res = await runAction('Iterate Experiment', [
            { Name: 'CompanyID', Value: this.experiment.CompanyID },
            { Name: 'ParentExperimentID', Value: this.experiment.ID },
            { Name: 'NewName', Value: `${baseName} → ${variant.Name}` },
            { Name: 'NewHypothesis', Value: `Promoting winning variant "${variant.Name}". ${variant.Description ?? ''}`.trim() },
            { Name: 'NewDecisionDate', Value: newDecisionDate },
        ]);
        this.promotingVariantId = null;
        if (res.success) {
            this.busyMessage = `Promoted "${variant.Name}" — opened follow-up experiment.`;
            await this.load();
        } else {
            this.busyError = true;
            this.busyMessage = `Promote failed (${res.resultCode}): ${res.message}`;
        }
        this.busy = false;
        this.cdr.detectChanges();
    }

    async onComplete(toStatus: string): Promise<void> {
        // Note: Complete Experiment doesn't accept TargetStatus — it derives the
        // terminal state from the experiment's existing decision (Keep → Evergreen,
        // Kill → Completed). The toStatus arg is informational for the UI message.
        if (!this.experiment) return;
        this.busy = true;
        this.busyError = false;
        this.busyMessage = `Completing experiment to ${toStatus}…`;
        this.cdr.detectChanges();
        const res = await runAction('Complete Experiment', [
            { Name: 'CompanyID', Value: this.experiment.CompanyID },
            { Name: 'ExperimentID', Value: this.experiment.ID },
        ]);
        if (res.success) {
            this.busyMessage = `Completed: ${res.message}`;
            await this.load();
        } else {
            this.busyError = true;
            this.busyMessage = `Complete failed (${res.resultCode}): ${res.message}`;
        }
        this.busy = false;
        this.cdr.detectChanges();
    }

    async onIterate(): Promise<void> {
        if (!this.experiment) return;
        this.busy = true;
        this.busyError = false;
        this.busyMessage = 'Creating iterate child…';
        this.cdr.detectChanges();
        const newDecisionDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
        const res = await runAction('Iterate Experiment', [
            { Name: 'CompanyID', Value: this.experiment.CompanyID },
            { Name: 'ParentExperimentID', Value: this.experiment.ID },
            { Name: 'NewName', Value: `${this.experiment.Name ?? this.experiment.Hypothesis?.slice(0, 60) ?? 'Experiment'} (iteration)` },
            { Name: 'NewHypothesis', Value: `${this.experiment.Hypothesis ?? ''} — refined based on prior result.` },
            { Name: 'NewDecisionDate', Value: newDecisionDate },
        ]);
        if (res.success) {
            this.busyMessage = `Iterate: ${res.message}`;
            await this.load();
        } else {
            this.busyError = true;
            this.busyMessage = `Iterate failed (${res.resultCode}): ${res.message}`;
        }
        this.busy = false;
        this.cdr.detectChanges();
    }
}
